import { faChevronLeft, faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons/faCheckCircle';
import { faTimesCircle } from '@fortawesome/pro-solid-svg-icons/faTimesCircle';
import useWindowSize from '@propertypal/shared/src/hooks/useWindowSize';
import { Property } from '@propertypal/shared/src/types';
import platform from '@propertypal/shared/src/utils/platform';
import React, { useEffect, useRef, useState } from 'react';
import FontAwesomeIcon from '../icons/FontAwesomeIcon';
import { Heading, TextLink } from '../typography';
import {
  CellContainer,
  CellOverlay,
  Container,
  ScrollButton,
  ScrollContainer,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooterCells,
  TableHead,
  TableHeader,
  TableRow,
  Wrapper,
} from './PromoteTable.style';

type FeatureCell = { featured?: boolean; text?: string | JSX.Element };

type FeatureLink = { text: string; href: string };

type Feature = {
  name: string | JSX.Element;
  premier: FeatureCell;
  featured: FeatureCell;
  isBoost?: boolean;
  link?: FeatureLink;
};

const features: Feature[] = [
  {
    name: 'Listing duration',
    premier: { featured: true, text: 'Lifetime of the listing' },
    featured: { featured: true, text: 'Chosen duration' },
  },
  { name: 'Increased exposure', premier: { featured: true }, featured: { featured: true } },
  {
    name: 'Listing boost',
    premier: { featured: true, text: '4 boosts' },
    featured: { featured: true, text: 'Chosen areas' },
  },
  {
    name: `Enhanced listing on ${platform.select('PropertyPal & Propertynews', 'Propertynews & PropertyPal')}`,
    premier: { featured: true },
    featured: { featured: true },
  },
  {
    name: 'Option for additional boosts',
    premier: { featured: true, text: '2 boosts' },
    featured: { featured: false },
    isBoost: true,
    link: { href: '/promote/boost', text: '(Premier Boost)' },
  },
  {
    name: 'Bigger listing',
    premier: { featured: true, text: 'Extra large' },
    featured: { featured: false, text: 'Standard' },
  },
  {
    name: 'Bigger photos',
    premier: { featured: true, text: 'Large photos' },
    featured: { featured: false, text: 'Standard photos' },
  },
  {
    name: 'Promotional tagline',
    premier: { featured: true },
    featured: { featured: false },
  },
  { name: 'Extra photos', premier: { featured: true }, featured: { featured: false } },
  {
    name: (
      <>
        <TableFooterCells uppercase>
          <b>Sales</b> Property Price
        </TableFooterCells>
        <TableFooterCells uppercase>
          <b>Rental</b> Property Price
        </TableFooterCells>
      </>
    ),
    premier: {
      text: (
        <>
          <TableFooterCells>
            <b>&pound;99</b>
          </TableFooterCells>
          <TableFooterCells>
            <b>&pound;49</b>
          </TableFooterCells>
        </>
      ),
    },
    featured: {
      text: (
        <>
          <TableFooterCells>
            From <b>&pound;24</b>
          </TableFooterCells>
          <TableFooterCells>
            From <b>&pound;12</b>
          </TableFooterCells>
        </>
      ),
    },
  },
];

const PromoteTableCell: React.FC<FeatureCell> = ({ featured, text }) => {
  return (
    <CellContainer>
      {featured != null && featured && <FontAwesomeIcon icon={faCheckCircle} size={24} color="#7ec07c" />}
      {featured != null && !featured && <FontAwesomeIcon icon={faTimesCircle} size={24} color="#d0021b" />}
      {text != null && <p>{text}</p>}
    </CellContainer>
  );
};

interface PromoteTableProps {
  highlightIndex?: number;
  boost?: boolean;
  property?: Property | null;
}

const PromoteTable: React.FC<PromoteTableProps> = ({ highlightIndex, boost, property }) => {
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [showScrollLeft, setShowScrollLeft] = useState(false);
  const [showScrollRight, setShowScrollRight] = useState(false);
  const [headerOffsets, setHeaderOffsets] = useState<number[]>([]);

  const windowSize = useWindowSize();

  const scrollLeft = () => {
    const container = tableContainerRef.current;
    if (container) {
      const currentScroll = container.scrollLeft;
      const prevHeaderOffset = [...headerOffsets].reverse().find((offset) => offset < currentScroll);
      container.scrollTo({
        left: prevHeaderOffset ?? 0,
        behavior: 'smooth',
      });
    }
  };

  const scrollRight = () => {
    const container = tableContainerRef.current;
    if (container) {
      const currentScroll = container.scrollLeft;
      const nextHeaderOffset = headerOffsets.find((offset) => offset > currentScroll + 10); // Add a small buffer

      if (nextHeaderOffset !== undefined) {
        container.scrollTo({
          left: nextHeaderOffset,
          behavior: 'smooth',
        });
      } else {
        // If no next offset is found, it means we're at the last header
        container.scrollTo({
          left: container.scrollWidth - container.clientWidth,
          behavior: 'smooth',
        });
      }
    }
  };

  // Function to update scroll visibility
  const updateScrollVisibility = async () => {
    const container = tableContainerRef.current;
    if (container) {
      setShowScrollLeft(container.scrollLeft > 0);
      setShowScrollRight(container.scrollLeft < container.scrollWidth - container.clientWidth);
    }
  };

  // UseEffect to get header positions and update on scroll
  useEffect(() => {
    const container = tableContainerRef.current;
    if (container) {
      const headers = Array.from(container.querySelectorAll('thead th')) as HTMLTableCellElement[];
      setHeaderOffsets(headers.map((header) => header.offsetLeft));
      updateScrollVisibility();
      container.addEventListener('scroll', updateScrollVisibility);
    }
    return () => {
      if (container) container.removeEventListener('scroll', updateScrollVisibility);
    };
  }, []);

  useEffect(() => {
    updateScrollVisibility();
  }, [windowSize?.width]);

  return (
    <Container data-testid="promote-table">
      <Wrapper>
        <Heading align="center" fontSize={36}>
          Compare our listing promotions
        </Heading>

        {(showScrollLeft || showScrollRight) && (
          <ScrollContainer>
            {showScrollLeft && (
              <ScrollButton type="button" onClick={scrollLeft} aria-label="Scroll Left" data-testid="scroll-left">
                <FontAwesomeIcon icon={faChevronLeft} size={18} />
              </ScrollButton>
            )}
            {showScrollRight && (
              <ScrollButton type="button" onClick={scrollRight} aria-label="Scroll Right" data-testid="scroll-right">
                <FontAwesomeIcon icon={faChevronRight} size={18} />
              </ScrollButton>
            )}
          </ScrollContainer>
        )}

        <TableContainer
          ref={tableContainerRef}
          data-testid="promote-table-container"
          hasScrollBtns={showScrollLeft || showScrollRight}
        >
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead leftAlign>LISTING FEATURES</TableHead>
                <TableHead highlight={highlightIndex === 1}>PREMIER LISTING</TableHead>
                <TableHead highlight={highlightIndex === 2}>FEATURED LISTING</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {features.map((feature, index) => {
                const isBoost = feature.isBoost && boost;
                return (
                  // eslint-disable-next-line react/no-array-index-key
                  <TableRow key={index}>
                    <TableCell
                      leftAlign
                      bold
                      highlight={isBoost}
                      isBoost={isBoost}
                      data-testid={isBoost ? 'boost-highlight' : undefined}
                    >
                      <span>{feature.name}</span>
                      {feature.link && !isBoost && (
                        <TextLink
                          prefetch={false}
                          href={`${feature.link?.href}${property ? `?propertyId=${property.pathId}` : ''}`}
                          display="block"
                          fontWeight="400"
                        >
                          {feature.link?.text}
                        </TextLink>
                      )}
                    </TableCell>
                    <TableCell
                      highlight={highlightIndex === 1}
                      isBoost={isBoost}
                      lastRow={index === features.length - 1}
                    >
                      {highlightIndex === 1 && <CellOverlay data-testid="premier-highlight" />}
                      <PromoteTableCell {...feature.premier} />
                    </TableCell>
                    <TableCell
                      highlight={highlightIndex === 2}
                      isBoost={isBoost}
                      lastRow={index === features.length - 1}
                      lastCell
                    >
                      {highlightIndex === 2 && <CellOverlay data-testid="featured-highlight" />}
                      <PromoteTableCell {...feature.featured} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Wrapper>
    </Container>
  );
};

export default PromoteTable;
